/**
 * P2 (ClickUp 86d3yf9q7) — the single generator for the initial credential a
 * newly created patient logs in with.
 *
 * It replaces two verbatim copies of a `Math.random()` generator that were
 * already issuing real patient credentials (QuickCreatePatientModal and
 * CreateTreatmentPlanModal). `Math.random()` is not a CSPRNG — its stream is
 * seeded and recoverable from a few observed outputs — so a credential drawn
 * from it is not safe to email to a patient as their login.
 * `crypto.getRandomValues` is, and is available in every supported browser and
 * in the jsdom test environment.
 *
 * Nothing here logs, stores or returns anything but the value itself: the
 * caller sends it once in the create payload and once in the welcome
 * notification, and never reads it back.
 */

/**
 * Ambiguous glyphs (I, l, 1, O, 0) are excluded so a password read off an
 * emailed or printed welcome message can be typed back correctly. Retained
 * unchanged from the generator this replaces.
 */
const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';

/** Comfortably clear of the authentication service's `min:8` password rule. */
export const GENERATED_PASSWORD_LENGTH = 12;

export const generatePassword = (length: number = GENERATED_PASSWORD_LENGTH): string => {
	// Uint32Array rather than Uint8Array: folding a random integer into a
	// 59-character alphabet with `%` favours the first (2^bits % 59) characters.
	// Over 8 bits that is a real ~25% skew across 20 of the 59 glyphs; over 32
	// bits it is roughly 1 part in 7x10^7 and not worth rejection sampling.
	const randomValues = new Uint32Array(length);
	crypto.getRandomValues(randomValues);

	return Array.from(randomValues, (value) => PASSWORD_ALPHABET[value % PASSWORD_ALPHABET.length]).join('');
};
